<script setup lang="ts">
import { computed } from 'vue';

import { authClient } from '@/api-client';
import EventBus from '@/event-bus';
import { KaMenu } from '@/kasten-components';
import { info as logInfo } from '@/logger';
import { defaultErrorHandler } from '@/services';
import { useAppInfoStore } from '@/stores/appInfo';
import { useUserStore } from '@/stores/user';
import { cnMerge, getUsernameDisplay } from '@/utils';
import { routeTo, routeToResolvedGeneric } from '@/utils/cluster-utils';
import IconChevronDown from '~icons/lucide/chevron-down';
import IconLogout from '~icons/lucide/log-out';
import IconUser from '~icons/lucide/user-round';

const AuthMethod = {
  BASIC: 'basic',
  OIDC: 'oidc',
  TOKEN: 'token',
  NONE: 'none',
} as const;

const appInfoStore = useAppInfoStore();
const userStore = useUserStore();

const usernameDisplay = computed(() => {
  const username = userStore.authUser?.username ?? '';
  return getUsernameDisplay(username);
});

const showSignOut = computed(() => {
  // authType should always be based on the hostSystemInfo if it exists.
  const authType =
    appInfoStore.hostSystemInfo !== null
      ? appInfoStore.hostSystemInfo.authType
      : appInfoStore.systemInfo?.authType;
  return [AuthMethod.OIDC, AuthMethod.TOKEN].some((variant) => variant === authType);
});

const logout = () => {
  logInfo('Logout clicked');

  authClient.logout
    .logout()
    .then(() => {
      logInfo('Logout successful. Redirecting..');
      userStore.logOut();
      routeTo({ name: 'Logout' });
    })
    .catch(defaultErrorHandler);
};
</script>

<template>
  <KaMenu
    :label="usernameDisplay"
    items-z-index-classes="z-40"
    items-classes="shadow-xl dark:ring-navy-600 dark:bg-[#13203A]"
    button-classes="inline sm:flex h-10 bg-transparent p-2 text-gray-700 shadow-none ring-0 hover:bg-gray-100 hover:text-gray-900 normal-case dark:bg-transparent dark:text-gray-400 dark:ring-0 dark:hover:bg-navy-800 dark:hover:text-gray-50 focus-visible:outline-offset-0 font-normal"
    aligned="right"
    data-testid="user-menu"
  >
    <template #label>
      <IconUser class="-mt-0.5 size-4 sm:mr-2" aria-hidden="true" />
      <span class="max-w-64 truncate max-sm:sr-only">
        {{ usernameDisplay }}
      </span>
    </template>
    <template #right>
      <IconChevronDown class="-mr-1 hidden size-4 text-gray-500 sm:inline" />
    </template>
    <div class="px-4 py-3" v-if="userStore.authUser?.username">
      <div class="text-xs text-gray-600 dark:text-gray-400">Username</div>
      <div class="break-words text-sm">{{ userStore.authUser.username }}</div>
    </div>
    <KaMenu.Group>
      <KaMenu.Item
        as="link"
        :link-props="{ to: routeToResolvedGeneric({ name: 'UserProfile' }) }"
        :class="
          cnMerge(
            'group inline-flex w-full items-center text-left text-sm text-gray-900 no-underline dark:text-gray-50',
          )
        "
        data-testid=""
      >
        <IconUser class="mr-3 size-5 text-gray-500" aria-hidden="true" />
        User Profile
      </KaMenu.Item>

      <KaMenu.Item
        :left="{ icon: IconLogout, classes: 'text-gray-500' }"
        @click="EventBus.$emit('start-guided-tour')"
      >
        Launch Guided Tour
      </KaMenu.Item>
    </KaMenu.Group>

    <KaMenu.Item danger @click="logout" v-if="showSignOut" data-testid="logout-button">
      Log out
    </KaMenu.Item>
  </KaMenu>
</template>
